// src/components/AdvancedAnimatedMap/AdvancedAnimatedMap.jsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "./AdvancedAnimatedMap.css";

// Apple-inspired color palette for daily routes
const DAILY_COLORS = [
  {
    color: "#007AFF",
    name: "Day 1",
    gradient: "linear-gradient(135deg, #007AFF, #5AC8FA)",
  },
  {
    color: "#34C759",
    name: "Day 2",
    gradient: "linear-gradient(135deg, #34C759, #30D158)",
  },
  {
    color: "#FF9500",
    name: "Day 3",
    gradient: "linear-gradient(135deg, #FF9500, #FFCC02)",
  },
  {
    color: "#FF2D92",
    name: "Day 4",
    gradient: "linear-gradient(135deg, #FF2D92, #FF375F)",
  },
  {
    color: "#AF52DE",
    name: "Day 5",
    gradient: "linear-gradient(135deg, #AF52DE, #BF5AF2)",
  },
  {
    color: "#5AC8FA",
    name: "Day 6",
    gradient: "linear-gradient(135deg, #5AC8FA, #007AFF)",
  },
  {
    color: "#32D74B",
    name: "Day 7",
    gradient: "linear-gradient(135deg, #32D74B, #34C759)",
  },
  {
    color: "#FF3B30",
    name: "Day 8",
    gradient: "linear-gradient(135deg, #FF3B30, #FF375F)",
  },
  {
    color: "#64D2FF",
    name: "Day 9",
    gradient: "linear-gradient(135deg, #64D2FF, #5AC8FA)",
  },
  {
    color: "#30B0C7",
    name: "Day 10",
    gradient: "linear-gradient(135deg, #30B0C7, #32ADE6)",
  },
];

// Route service with improved Apple-style architecture
class RouteService {
  constructor() {
    this.cache = new Map();
    this.requestQueue = [];
    this.isProcessing = false;
    this.rateLimitDelay = 1000;
  }

  getCacheKey(start, end, mode) {
    return `${start[0]},${start[1]}-${end[0]},${end[1]}-${mode}`;
  }

  async getRouteFromOSRM(start, end, mode = "driving") {
    const profile = mode === "walking" ? "walking" : "driving";
    const url = `https://router.project-osrm.org/route/v1/${profile}/${start[1]},${start[0]};${end[1]},${end[0]}?overview=full&geometries=geojson`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error(`OSRM API failed with status: ${response.status}`);
      }

      const data = await response.json();

      if (data.routes && data.routes.length > 0) {
        const coordinates = data.routes[0].geometry.coordinates;
        const convertedCoords = coordinates.map((coord) => [
          coord[1],
          coord[0],
        ]);
        return convertedCoords;
      }

      return null;
    } catch (error) {
      console.error("OSRM API request failed:", error);
      return null;
    }
  }

  generateCurvedPath(start, end, points = 10) {
    const path = [];
    const latDiff = end[0] - start[0];
    const lngDiff = end[1] - start[1];

    const curveFactor = 0.3;
    const midLat = start[0] + latDiff * 0.5;
    const midLng = start[1] + lngDiff * 0.5;

    const controlLat =
      midLat + (Math.random() - 0.5) * Math.abs(latDiff) * curveFactor;
    const controlLng =
      midLng + (Math.random() - 0.5) * Math.abs(lngDiff) * curveFactor;

    for (let i = 0; i <= points; i++) {
      const t = i / points;
      const lat =
        Math.pow(1 - t, 2) * start[0] +
        2 * (1 - t) * t * controlLat +
        Math.pow(t, 2) * end[0];
      const lng =
        Math.pow(1 - t, 2) * start[1] +
        2 * (1 - t) * t * controlLng +
        Math.pow(t, 2) * end[1];
      path.push([lat, lng]);
    }

    return path;
  }

  calculateDistance(start, end) {
    const R = 6371;
    const dLat = ((end[0] - start[0]) * Math.PI) / 180;
    const dLon = ((end[1] - start[1]) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((start[0] * Math.PI) / 180) *
        Math.cos((end[0] * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  getOptimalTransportMode(start, end) {
    const distance = this.calculateDistance(start, end);
    return distance < 1 ? "walking" : "driving";
  }

  async getRoute(start, end, mode = "auto") {
    if (mode === "auto") {
      mode = this.getOptimalTransportMode(start, end);
    }

    const cacheKey = this.getCacheKey(start, end, mode);

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const distance = this.calculateDistance(start, end);
    if (distance < 0.1) {
      const straightLine = [start, end];
      this.cache.set(cacheKey, { coordinates: straightLine, mode, distance });
      return { coordinates: straightLine, mode, distance };
    }

    return new Promise((resolve) => {
      this.requestQueue.push({ start, end, mode, cacheKey, resolve });
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessing || this.requestQueue.length === 0) return;

    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      const { start, end, mode, cacheKey, resolve } = this.requestQueue.shift();

      try {
        let coordinates = await this.getRouteFromOSRM(start, end, mode);

        if (!coordinates || coordinates.length < 2) {
          coordinates = this.generateCurvedPath(start, end);
        }

        const distance = this.calculateDistance(start, end);
        const routeData = {
          coordinates,
          mode: coordinates.length === 2 ? "direct" : mode,
          distance,
        };

        this.cache.set(cacheKey, routeData);
        resolve(routeData);

        if (this.requestQueue.length > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, this.rateLimitDelay)
          );
        }
      } catch (error) {
        console.error("Route fetch failed:", error);
        const distance = this.calculateDistance(start, end);
        const fallbackRoute = { coordinates: [start, end], mode, distance };
        this.cache.set(cacheKey, fallbackRoute);
        resolve(fallbackRoute);
      }
    }

    this.isProcessing = false;
  }

  clearCache() {
    this.cache.clear();
  }
}

const routeService = new RouteService();

// Smart camera follow component with Apple-style animations
function SmartCameraFollow({
  locations,
  markerDelay = 600,
  onAnimationStart,
  onAnimationEnd,
}) {
  const map = useMap();
  const [currentFocus, setCurrentFocus] = useState(null);

  const locationsRef = useRef();
  const isMountedRef = useRef(true);
  const timeoutsRef = useRef([]);
  const animationLockRef = useRef(false);
  const animationCompletedRef = useRef(false);
  const currentAnimationIdRef = useRef(null);

  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    timeoutsRef.current = [];
  }, []);

  const safeMapOperation = useCallback(
    (operation, operationName = "unknown") => {
      if (!isMountedRef.current || !map) {
        return false;
      }

      try {
        const container = map.getContainer();
        if (!container || !container.parentNode) {
          return false;
        }

        operation();
        return true;
      } catch (error) {
        console.error(`Error in ${operationName}:`, error);
        return false;
      }
    },
    [map]
  );

  const groupLocationsByCity = (locations) => {
    const groups = [];
    const montrealGroup = [];
    const quebecGroup = [];
    const otherGroups = {};

    locations.forEach((location, index) => {
      const { lat, lng, name } = location;

      if (lat >= 45.4 && lat <= 45.7 && lng >= -73.8 && lng <= -73.4) {
        montrealGroup.push({ ...location, originalIndex: index });
      } else if (lat >= 46.7 && lat <= 46.9 && lng >= -71.4 && lng <= -71.1) {
        quebecGroup.push({ ...location, originalIndex: index });
      } else {
        const cityKey = `${Math.round(lat * 10)}-${Math.round(lng * 10)}`;
        if (!otherGroups[cityKey]) {
          otherGroups[cityKey] = [];
        }
        otherGroups[cityKey].push({ ...location, originalIndex: index });
      }
    });

    if (montrealGroup.length > 0) {
      groups.push({
        name: "Montreal",
        locations: montrealGroup,
        center: [45.5017, -73.5673],
        zoom: 13,
      });
    }

    if (quebecGroup.length > 0) {
      groups.push({
        name: "Quebec City",
        locations: quebecGroup,
        center: [46.8139, -71.208],
        zoom: 13,
      });
    }

    Object.values(otherGroups).forEach((group) => {
      if (group.length > 0) {
        const avgLat =
          group.reduce((sum, loc) => sum + loc.lat, 0) / group.length;
        const avgLng =
          group.reduce((sum, loc) => sum + loc.lng, 0) / group.length;
        groups.push({
          name: `Location`,
          locations: group,
          center: [avgLat, avgLng],
          zoom: 12,
        });
      }
    });

    return groups;
  };

  useEffect(() => {
    isMountedRef.current = true;

    if (!locations || locations.length === 0) {
      safeMapOperation(() => {
        map.setView([45.5017, -73.5673], 12, { animate: true, duration: 1 });
      }, "initial-view-setting");
      return;
    }

    const locationsKey = locations
      .map((l) => `${l.lat}-${l.lng}-${l.name}`)
      .join(",");
    const prevLocationsKey = locationsRef.current
      ?.map((l) => `${l.lat}-${l.lng}-${l.name}`)
      .join(",");

    const isDataSame = locationsKey === prevLocationsKey;
    const isAnimationInProgress = animationLockRef.current;
    const isAnimationCompleted = animationCompletedRef.current && isDataSame;

    if (isAnimationCompleted || isAnimationInProgress) {
      return;
    }

    animationCompletedRef.current = false;
    animationLockRef.current = true;

    const animationId = `animation-${Date.now()}-${Math.random()}`;
    currentAnimationIdRef.current = animationId;

    if (onAnimationStart) {
      onAnimationStart();
    }

    clearAllTimeouts();
    locationsRef.current = locations;

    const cityGroups = groupLocationsByCity(locations);
    let currentDelay = 0;

    cityGroups.forEach((group, groupIndex) => {
      const cityTimeout = setTimeout(() => {
        if (
          !isMountedRef.current ||
          currentAnimationIdRef.current !== animationId
        ) {
          return;
        }

        setCurrentFocus(`Moving to ${group.name}`);

        safeMapOperation(() => {
          map.setView(group.center, group.zoom, {
            animate: true,
            duration: 1.5,
            easeLinearity: 0.3,
          });
        }, `city-switch-to-${group.name}`);
      }, currentDelay);

      timeoutsRef.current.push(cityTimeout);
      currentDelay += 1500;

      group.locations.forEach((location, locationIndex) => {
        const locationTimeout = setTimeout(() => {
          if (
            !isMountedRef.current ||
            currentAnimationIdRef.current !== animationId
          ) {
            return;
          }

          setCurrentFocus(location.name);

          let targetZoom = group.zoom;
          if (locationIndex === 0) {
            targetZoom = Math.min(group.zoom + 1, 16);
          }

          safeMapOperation(() => {
            map.setView([location.lat, location.lng], targetZoom, {
              animate: true,
              duration: 0.8,
              easeLinearity: 0.4,
            });
          }, `location-focus-${location.name}`);
        }, currentDelay);

        timeoutsRef.current.push(locationTimeout);
        currentDelay += markerDelay;
      });

      if (groupIndex < cityGroups.length - 1) {
        currentDelay += 800;
      }
    });

    const finalTimeout = setTimeout(() => {
      if (
        !isMountedRef.current ||
        currentAnimationIdRef.current !== animationId
      ) {
        return;
      }

      setCurrentFocus("Complete Journey");

      safeMapOperation(() => {
        const bounds = locations.map((l) => [l.lat, l.lng]);
        map.fitBounds(bounds, {
          padding: [60, 60],
          maxZoom: 10,
          animate: true,
          duration: 3,
          easeLinearity: 0.2,
        });
      }, "final-journey-view");
    }, currentDelay + 1000);

    timeoutsRef.current.push(finalTimeout);

    const unlockTimeout = setTimeout(() => {
      if (currentAnimationIdRef.current === animationId) {
        animationLockRef.current = false;
        animationCompletedRef.current = true;

        if (onAnimationEnd) {
          onAnimationEnd();
        }
      }
    }, currentDelay + 1000 + 3000 + 1000);

    timeoutsRef.current.push(unlockTimeout);

    return () => {
      isMountedRef.current = false;
      clearAllTimeouts();

      if (currentAnimationIdRef.current === animationId) {
        animationLockRef.current = false;
        currentAnimationIdRef.current = null;

        if (onAnimationEnd) {
          onAnimationEnd();
        }
      }
    };
  }, [locations, map, markerDelay, safeMapOperation, clearAllTimeouts]);

  return null;
}

// Apple-style animated marker component
const AdvancedAnimatedMarker = ({
  position,
  name,
  delay = 0,
  index,
  activityData,
  onMarkerClick,
  dayIndex = 0,
}) => {
  const [animationPhase, setAnimationPhase] = useState("hidden");
  const [showTrail, setShowTrail] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const isMountedRef = useRef(true);
  const timeoutsRef = useRef([]);

  useEffect(() => {
    isMountedRef.current = true;

    if (hasAnimated) {
      setAnimationPhase("visible");
      return;
    }

    const sequence = async () => {
      const timeout1 = setTimeout(() => {
        if (!isMountedRef.current) return;
        setAnimationPhase("dropping");
      }, delay);
      timeoutsRef.current.push(timeout1);

      const timeout2 = setTimeout(() => {
        if (!isMountedRef.current) return;
        setAnimationPhase("landed");
        setShowTrail(true);
      }, delay + 800);
      timeoutsRef.current.push(timeout2);

      const timeout3 = setTimeout(() => {
        if (!isMountedRef.current) return;
        setAnimationPhase("visible");
        setHasAnimated(true);
      }, delay + 1100);
      timeoutsRef.current.push(timeout3);
    };

    sequence();

    return () => {
      isMountedRef.current = false;
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current = [];
    };
  }, [delay, name, hasAnimated]);

  const getMarkerStyle = () => {
    const dayColor = DAILY_COLORS[dayIndex % DAILY_COLORS.length];

    if (activityData) {
      if (
        activityData.cuisine ||
        activityData.activity_desc?.toLowerCase().includes("restaurant") ||
        activityData.activity_desc?.toLowerCase().includes("food")
      ) {
        return {
          color: dayColor.color,
          category: "restaurant",
          gradient: dayColor.gradient,
        };
      } else if (
        activityData.activity_desc?.toLowerCase().includes("museum") ||
        activityData.activity_desc?.toLowerCase().includes("park") ||
        activityData.activity_desc?.toLowerCase().includes("church")
      ) {
        return {
          color: dayColor.color,
          category: "attraction",
          gradient: dayColor.gradient,
        };
      } else if (
        activityData.location?.toLowerCase().includes("hotel") ||
        activityData.activity?.toLowerCase().includes("hotel") ||
        activityData.place_id === "hotel_checkin"
      ) {
        return {
          color: dayColor.color,
          category: "hotel",
          gradient: dayColor.gradient,
        };
      } else if (
        activityData.activity_desc?.toLowerCase().includes("shop") ||
        activityData.activity_desc?.toLowerCase().includes("market")
      ) {
        return {
          color: dayColor.color,
          category: "shopping",
          gradient: dayColor.gradient,
        };
      }
    }
    return {
      color: dayColor.color,
      category: "default",
      gradient: dayColor.gradient,
    };
  };

  const markerStyle = getMarkerStyle();

  const advancedIcon = L.divIcon({
    html: `
            <div class="apple-marker ${animationPhase}" data-category="${
      markerStyle.category
    }" data-day="${dayIndex + 1}">
                ${showTrail ? '<div class="marker-trail"></div>' : ""}
                <div class="marker-container">
                    <div class="marker-pin" style="background: ${
                      markerStyle.gradient
                    };">
                        <div class="marker-pulse" style="background: ${
                          markerStyle.color
                        };"></div>
                    </div>
                    <div class="marker-label">
                        <div class="label-content">
                            <span class="label-number" style="background: ${
                              markerStyle.color
                            };">${index + 1}</span>
                            <span class="label-text">${name}</span>
                        </div>
                        ${
                          activityData?.start_time
                            ? `<div class="label-time">${activityData.start_time}</div>`
                            : ""
                        }
                        <div class="label-day" style="color: ${
                          markerStyle.color
                        };">Day ${dayIndex + 1}</div>
                    </div>
                </div>
            </div>
        `,
    className: "apple-marker-container",
    iconSize: [50, 90],
    iconAnchor: [25, 90],
  });

  if (animationPhase === "hidden") return null;

  return (
    <Marker
      position={position}
      icon={advancedIcon}
      eventHandlers={{
        click: () => {
          if (isMountedRef.current && onMarkerClick) {
            onMarkerClick(activityData, name, index + 1);
          }
        },
      }}
    />
  );
};

// Real route animated polylines component
const RealRouteAnimatedPolylines = ({ locations, itinerary, delay = 2000 }) => {
  const [visibleRoutes, setVisibleRoutes] = useState([]);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  const [routeProgress, setRouteProgress] = useState({
    dayIndex: 0,
    segmentIndex: 0,
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const isMountedRef = useRef(true);
  const timeoutsRef = useRef([]);
  const routesRef = useRef([]);

  const dailyRoutes = React.useMemo(() => {
    if (!locations || !itinerary || locations.length === 0) {
      return [];
    }

    const routes = [];
    let locationIndex = 0;

    itinerary.forEach((day, dayIndex) => {
      const dayActivities = day.plan?.length || 0;
      const dayPositions = [];

      for (
        let i = 0;
        i < dayActivities && locationIndex < locations.length;
        i++
      ) {
        const location = locations[locationIndex];
        if (
          location &&
          typeof location.lat === "number" &&
          typeof location.lng === "number"
        ) {
          dayPositions.push([location.lat, location.lng]);
        }
        locationIndex++;
      }

      if (dayPositions.length > 1) {
        routes.push({
          dayIndex,
          positions: dayPositions,
          color: DAILY_COLORS[dayIndex % DAILY_COLORS.length].color,
          dayName: `Day ${dayIndex + 1}`,
          segments: [],
        });
      }
    });

    return routes;
  }, [locations, itinerary]);

  const loadRealRoutes = useCallback(async () => {
    if (dailyRoutes.length === 0 || hasAnimated) {
      return routesRef.current;
    }

    setIsLoadingRoutes(true);

    const routesWithRealPaths = [];

    for (const route of dailyRoutes) {
      const routeSegments = [];

      for (let i = 0; i < route.positions.length - 1; i++) {
        const start = route.positions[i];
        const end = route.positions[i + 1];

        try {
          const routeData = await routeService.getRoute(start, end, "auto");

          if (
            routeData &&
            routeData.coordinates &&
            routeData.coordinates.length > 1
          ) {
            routeSegments.push({
              start,
              end,
              coordinates: routeData.coordinates,
              mode: routeData.mode,
              distance: routeData.distance,
            });
          } else {
            throw new Error("Invalid route data");
          }
        } catch (error) {
          const curvedPath = routeService.generateCurvedPath(start, end);
          routeSegments.push({
            start,
            end,
            coordinates: curvedPath,
            mode: "curved",
            distance: routeService.calculateDistance(start, end),
          });
        }
      }

      routesWithRealPaths.push({
        ...route,
        segments: routeSegments,
      });
    }

    routesRef.current = routesWithRealPaths;
    setIsLoadingRoutes(false);

    return routesWithRealPaths;
  }, [dailyRoutes, hasAnimated]);

  const animateRoutes = useCallback(
    async (routes) => {
      if (!routes || routes.length === 0) {
        return;
      }

      let animationDelay = delay;

      for (let dayIndex = 0; dayIndex < routes.length; dayIndex++) {
        const route = routes[dayIndex];

        setTimeout(() => {
          if (!isMountedRef.current) return;

          setRouteProgress({ dayIndex, segmentIndex: 0 });

          let segmentDelay = 0;
          route.segments.forEach((segment, segmentIndex) => {
            setTimeout(() => {
              if (!isMountedRef.current) return;

              const routeId = `day-${dayIndex}-segment-${segmentIndex}`;

              setVisibleRoutes((prev) => {
                const existingRoute = prev.find((r) => r.id === routeId);
                if (existingRoute) {
                  return prev;
                }

                const newRoute = {
                  id: routeId,
                  coordinates: segment.coordinates,
                  color: route.color,
                  dayIndex,
                  segmentIndex,
                  mode: segment.mode,
                  distance: segment.distance,
                };

                return [...prev, newRoute];
              });

              setRouteProgress({ dayIndex, segmentIndex: segmentIndex + 1 });
            }, segmentDelay);

            segmentDelay += 800;
          });
        }, animationDelay);

        animationDelay += route.segments.length * 800 + 1000;
      }

      setTimeout(() => {
        if (!isMountedRef.current) return;
        setHasAnimated(true);
      }, animationDelay + 1000);
    },
    [delay]
  );

  useEffect(() => {
    isMountedRef.current = true;

    if (hasAnimated || dailyRoutes.length === 0) {
      return;
    }

    setVisibleRoutes([]);
    setRouteProgress({ dayIndex: 0, segmentIndex: 0 });

    const initializeRoutes = async () => {
      const routes = await loadRealRoutes();
      if (routes && routes.length > 0 && isMountedRef.current) {
        await animateRoutes(routes);
      }
    };

    initializeRoutes();

    return () => {
      isMountedRef.current = false;
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current = [];
      setVisibleRoutes([]);
    };
  }, [dailyRoutes, loadRealRoutes, animateRoutes, hasAnimated]);

  if (isLoadingRoutes) {
    return <div className="route-loading-indicator">Loading routes...</div>;
  }

  return (
    <>
      {visibleRoutes.map((route, index) => {
        return (
          <Polyline
            key={`${route.id}-${index}`}
            positions={route.coordinates}
            pathOptions={{
              color: route.color,
              weight: route.mode === "walking" ? 3 : 4,
              opacity: 0.8,
              dashArray: route.mode === "walking" ? "8, 4" : "none",
              lineCap: "round",
              lineJoin: "round",
            }}
          />
        );
      })}
    </>
  );
};

// Main map component with Apple-style design
const AdvancedAnimatedMap = ({ locations, itinerary = [], onMarkerClick }) => {
  console.log("🗺️ AdvancedAnimatedMap 接收到：");
  console.log("  - locations prop:", locations);
  console.log("  - locations 类型:", typeof locations);
  console.log("  - locations 是数组:", Array.isArray(locations));
  console.log("  - locations 长度:", locations?.length);

  const [selectedMarker, setSelectedMarker] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [routeMode, setRouteMode] = useState("auto");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapRef = useRef();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (mapRef.current) {
        try {
          const container = mapRef.current.getContainer();
          mapRef.current.off();
          mapRef.current.remove();
          mapRef.current = null;

          if (container) {
            delete container._leaflet_id;
            delete container._leaflet_pos;
          }
        } catch (error) {
          console.error("Error cleaning up map:", error);
        }
      }
    };
  }, []);

  const validLocations = locations.filter(
    (loc) => typeof loc.lat === "number" && typeof loc.lng === "number"
  );

  console.log("🗺️ 过滤前 locations:", locations);
  console.log("🗺️ 过滤后 validLocations:", validLocations);
  console.log("🗺️ validLocations 数量:", validLocations.length);

  // 检查过滤掉的项目
  const invalidLocations = locations.filter(
    (loc) => typeof loc.lat !== "number" || typeof loc.lng !== "number"
  );
  if (invalidLocations.length > 0) {
    console.log("🚫 被过滤掉的位置:", invalidLocations);
  }

  const activityData = itinerary.flatMap((day) => day.plan || []);

  const getLocationDayIndex = (locationIndex) => {
    let totalActivities = 0;
    for (let dayIndex = 0; dayIndex < itinerary.length; dayIndex++) {
      const dayActivities = itinerary[dayIndex].plan?.length || 0;
      if (locationIndex < totalActivities + dayActivities) {
        return dayIndex;
      }
      totalActivities += dayActivities;
    }
    return 0;
  };

  const handleMarkerClick = useCallback(
    (data, name, number) => {
      if (!isMountedRef.current || isAnimating) {
        return;
      }

      setSelectedMarker({ data, name, number });

      if (onMarkerClick) {
        onMarkerClick(data, name, number);
      }
    },
    [onMarkerClick, isAnimating]
  );

  const handleClosePanel = useCallback(() => {
    if (!isMountedRef.current) return;
    setSelectedMarker(null);
  }, []);

  const handleAnimationStart = useCallback(() => {
    setIsAnimating(true);
  }, []);

  const handleAnimationEnd = useCallback(() => {
    setIsAnimating(false);
  }, []);

  const handleRouteModeChange = useCallback((newMode) => {
    setRouteMode(newMode);
    routeService.clearCache();
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);

    // Force map resize after fullscreen toggle
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 100);
  }, [isFullscreen]);

  // Handle fullscreen changes and map resize
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        setTimeout(() => {
          mapRef.current.invalidateSize();
        }, 100);
      }
    };

    if (isFullscreen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("resize", handleResize);
    } else {
      document.body.style.overflow = "";
      window.removeEventListener("resize", handleResize);
    }

    // Force resize when fullscreen state changes
    handleResize();

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("resize", handleResize);
    };
  }, [isFullscreen]);

  return (


    <div className={`apple-map-wrapper ${isFullscreen ? "fullscreen" : ""}`}>


        
      {isMountedRef.current && (
        
        <MapContainer
          ref={mapRef}
          center={[45.5017, -73.5673]}
          zoom={11}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
          zoomControl={false}
          dragging={true}
          doubleClickZoom={false}
          whenCreated={(mapInstance) => {
            if (isMountedRef.current) {
              mapRef.current = mapInstance;
            }
          }}
        >
          <SmartCameraFollow
            locations={validLocations}
            markerDelay={600}
            onAnimationStart={handleAnimationStart}
            onAnimationEnd={handleAnimationEnd}
          />

          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />

          {validLocations.map((loc, index) => {
            console.log(`🗺️ 准备渲染标记 ${index + 1}:`, {
              name: loc.name,
              position: [loc.lat, loc.lng],
              index: index,
            });
            const data = activityData[index] || null;
            const dayIndex = getLocationDayIndex(index);

            return (
              <AdvancedAnimatedMarker
                key={`${loc.name}-${index}-${loc.lat}-${loc.lng}`}
                position={[loc.lat, loc.lng]}
                name={loc.name}
                delay={index * 600}
                index={index}
                activityData={data}
                onMarkerClick={handleMarkerClick}
                dayIndex={dayIndex}
              />
            );
          })}

          <RealRouteAnimatedPolylines
            locations={validLocations}
            itinerary={itinerary}
            delay={validLocations.length * 600 + 1000}
          />
        </MapContainer>
      )}

      {/* Apple-style controls */}
      <div className="map-controls">
        <button
          className="control-button fullscreen-btn"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 2H2V6M10 14H14V10M14 6V2H10M2 10V14H6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 2H6V6M14 14H10V10M10 2H14V6M6 14H2V10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>

        <div className="route-mode-selector">
          <div className="selector-title">Route Mode</div>
          <div className="mode-buttons">
            {[
              { value: "auto", label: "Smart" },
              { value: "walking", label: "Walk" },
              { value: "driving", label: "Drive" },
            ].map((mode) => (
              <button
                key={mode.value}
                onClick={() => handleRouteModeChange(mode.value)}
                className={`mode-button ${
                  routeMode === mode.value ? "active" : ""
                }`}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Selected marker panel */}
      {selectedMarker && (
        <div className="marker-info-panel">
          <button className="close-button" onClick={handleClosePanel}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1L13 13M1 13L13 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div className="panel-header">
            <div className="marker-number">{selectedMarker.number}</div>
            <h3 className="panel-title">{selectedMarker.name}</h3>
          </div>

          {selectedMarker.data && (
            <div className="panel-content">
              {selectedMarker.data.activity_desc && (
                <p className="activity-description">
                  {selectedMarker.data.activity_desc}
                </p>
              )}

              <div className="activity-details">
                {selectedMarker.data.start_time &&
                  selectedMarker.data.end_time && (
                    <div className="detail-row">
                      <span className="detail-label">Time</span>
                      <span className="detail-value">
                        {selectedMarker.data.start_time} -{" "}
                        {selectedMarker.data.end_time}
                      </span>
                    </div>
                  )}

                {selectedMarker.data.avg_cost_per_person && (
                  <div className="detail-row">
                    <span className="detail-label">Cost</span>
                    <span className="detail-value">
                      ${selectedMarker.data.avg_cost_per_person} per person
                    </span>
                  </div>
                )}

                {selectedMarker.data.cuisine && (
                  <div className="detail-row">
                    <span className="detail-label">Cuisine</span>
                    <span className="detail-value">
                      {selectedMarker.data.cuisine}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="map-legend">
        <div className="legend-section">
          <h4 className="legend-title">Activity Types</h4>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-dot restaurant"></div>
              <span>Restaurant</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot attraction"></div>
              <span>Sightseeing</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot hotel"></div>
              <span>Hotel</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot shopping"></div>
              <span>Shopping</span>
            </div>
          </div>
        </div>

        <div className="legend-section">
          <h4 className="legend-title">Daily Routes</h4>
          <div className="legend-items">
            {DAILY_COLORS.slice(0, Math.min(itinerary.length, 5)).map(
              (day, index) => (
                <div key={index} className="legend-item">
                  <div
                    className="legend-line"
                    style={{ backgroundColor: day.color }}
                  ></div>
                  <span>{day.name}</span>
                </div>
              )
            )}
            {itinerary.length > 5 && (
              <div className="legend-item">
                <span>...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Animation indicator */}
      {isAnimating && (
        <div
          className={`animation-indicator ${
            isFullscreen ? "fullscreen-mode" : "normal-mode"
          }`}
        >
          <div className="indicator-content">
            <div className="loading-dots">
              <div></div>
              <div></div>
              <div></div>
            </div>
            <span>Generating your itinerary</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedAnimatedMap;
